import DOMPurify from "dompurify";
import React, { useState } from "react";
import { Rocket, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

export const LaunchAnnouncement = ({ onSent }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true);
    try { const { data } = await api.get("/admin/launch-announcement/preview"); setPreview(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); setOpen(false); }
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/launch-announcement/send"); toast.success(`Launch announcement sent to ${data.recipients} member(s)`); setOpen(false); onSent?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="glass flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold-light/60 p-5" data-testid="launch-card">
        <div>
          <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><Rocket size={18} /> Launch announcement</h3>
          <p className="text-sm text-muted-foreground">One tap: pinned “we’re live” notice + push + email to every active member with install-to-phone instructions.</p>
        </div>
        <Button onClick={load} className="bg-crimson" data-testid="launch-preview-btn"><Eye size={15} className="mr-1" /> Preview &amp; send</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="launch-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">{preview?.title || "Loading…"}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-palm" data-testid="launch-recipients">Will reach {preview.recipients} active member(s) via in-app notice, push and email.</p>
              <div>
                <p className="eyebrow text-gold">In-app / push text</p>
                <pre className="mt-1 whitespace-pre-wrap rounded-lg bg-parchment-card p-3 font-sans text-sm" data-testid="launch-body">{preview.body}</pre>
              </div>
              <div>
                <p className="eyebrow text-gold">Email preview</p>
                <div className="mt-1 overflow-hidden rounded-lg border border-gold/30" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.email_html, { USE_PROFILES: { html: true } }) }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy || !preview} className="bg-crimson" data-testid="launch-send-btn"><Rocket size={15} className="mr-1" /> {busy ? "Sending…" : "Send to all members"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
